// Cognito endpoints + client values come from /config.json loaded at boot
// (see src/config.ts + src/main.tsx). main.tsx awaits loadConfig() before
// rendering App, so getConfig() is safe to call from any handler below.
import { getConfig } from '../config';

const VERIFIER_KEY = 'cognito_code_verifier';

function base64urlEncode(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let str = '';
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

export function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64urlEncode(array.buffer);
}

export async function computeCodeChallenge(verifier: string): Promise<string> {
  const enc = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', enc);
  return base64urlEncode(digest);
}

export function buildAuthUrl(codeChallenge: string): string {
  const { cognitoDomain, cognitoClientId, cognitoRedirectUri } = getConfig();
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: cognitoClientId,
    redirect_uri: cognitoRedirectUri,
    scope: 'openid email profile',
    code_challenge_method: 'S256',
    code_challenge: codeChallenge,
  });
  return `https://${cognitoDomain}/oauth2/authorize?${params}`;
}

export async function redirectToSignIn(): Promise<void> {
  const verifier = generateCodeVerifier();
  const challenge = await computeCodeChallenge(verifier);
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  window.location.href = buildAuthUrl(challenge);
}

export interface TokenSet {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export async function exchangeCodeForTokens(code: string): Promise<TokenSet> {
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  if (!verifier) throw new Error('No code verifier found in session storage');
  sessionStorage.removeItem(VERIFIER_KEY);

  const { cognitoDomain, cognitoClientId, cognitoRedirectUri } = getConfig();
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: cognitoClientId,
    code,
    redirect_uri: cognitoRedirectUri,
    code_verifier: verifier,
  });

  const res = await fetch(`https://${cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Token exchange failed: ${text}`);
  }

  const data = await res.json() as {
    id_token: string;
    access_token: string;
    refresh_token: string;
    expires_in: number;
  };

  return {
    idToken: data.id_token,
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresIn: data.expires_in,
  };
}

export async function refreshTokens(refreshToken: string): Promise<TokenSet> {
  const { cognitoDomain, cognitoClientId } = getConfig();
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: cognitoClientId,
    refresh_token: refreshToken,
  });

  const res = await fetch(`https://${cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Token refresh failed: ${text}`);
  }

  const data = await res.json() as {
    id_token: string;
    access_token: string;
    refresh_token?: string;
    expires_in: number;
  };

  return {
    idToken: data.id_token,
    accessToken: data.access_token,
    refreshToken: data.refresh_token ?? refreshToken,
    expiresIn: data.expires_in,
  };
}

export function signOut(): void {
  sessionStorage.removeItem(VERIFIER_KEY);
  const { cognitoDomain, cognitoClientId, cognitoLogoutUri } = getConfig();
  const params = new URLSearchParams({
    client_id: cognitoClientId,
    logout_uri: cognitoLogoutUri || window.location.origin,
  });
  window.location.href = `https://${cognitoDomain}/logout?${params}`;
}
